import {withClientState} from 'apollo-link-state';
import {defaultDataIdFromObject, InMemoryCache} from 'apollo-cache-inmemory';
import {GetRepairsCart} from '../../../services/SmsService/query.graphql';
import {GetSelectedPaymentOption} from '../../../services/Customer/query.graphql';
import {GetCartStatus} from '../../../services/CartService/query.graphql';
import {GetRecentSearches} from '../../../services/SearchService/query.graphql';
import {getPromotionCode} from '../../cart/Promotion';

const getAddressCacheKey = obj => {
    if ( obj.id != null && String( obj.id ) !== '0' ) {
        return `${obj.__typename}:${obj.id}`;
    }

    if ( typeof obj.address1 === 'string' && obj.address1.length > 0 ) {
        return `${obj.__typename}:${obj.address1.replace( /[^a-z0-9]/gi, '' )}`;
    }

    return defaultDataIdFromObject( obj );
};

const MAX_RECENT_SEARCHES = 100;
const RECENT_SEARCHES_STORAGE_KEY = 'upi:recent-searches';

function normalizeRecentSearchQuery ( query ) {
    return String( query || '' ).trim().toLowerCase();
}

function createRecentSearchRecord ( query, createdAt = new Date().toISOString() ) {
    const trimmedQuery = String( query || '' ).trim();

    return {
        __typename: 'RecentSearch',
        query: trimmedQuery,
        normalizedQuery: normalizeRecentSearchQuery( trimmedQuery ),
        createdAt
    };
}

function getRecentSearchesFromStore ( cache ) {
    const store = cache.readQuery( {query: GetRecentSearches} ) || {};
    return Array.isArray( store.recentSearches ) ? store.recentSearches : [];
}

function readPersistedRecentSearches () {
    try {
        const serializedRecentSearches = window.localStorage.getItem( RECENT_SEARCHES_STORAGE_KEY );
        const recentSearches = JSON.parse( serializedRecentSearches );

        return Array.isArray( recentSearches ) ? recentSearches : [];
    }
    catch ( error ) {
        return [];
    }
}

function persistRecentSearches ( recentSearches ) {
    try {
        window.localStorage.setItem( RECENT_SEARCHES_STORAGE_KEY, JSON.stringify( recentSearches ) );
    }
    catch ( error ) {
        // ignore storage failures and keep the in-memory GraphQL state available
    }
}

export const cache = new InMemoryCache( {
    dataIdFromObject: obj => {
        switch ( obj.__typename ) {
            case 'Address':
                // addresses coming from SMS will always have an id of "0" which breaks apollo caching
                return getAddressCacheKey( obj );

            case 'CartLineItem':
                // case 'OrderLineItem': // note -> not in use
                return `${obj.__typename}:${obj.id}`;

            case 'Product':
                return `${obj.__typename}:${obj.sku}`;

            case 'Promotion':
                return getPromotionCode( obj )
                    ? `${obj.__typename}:${getPromotionCode( obj )}`
                    : null;

            case 'Taxonomy':
            case 'Distributor':
                return `${obj.__typename}:${obj.sysId}`;

            case 'Account':
            case 'Wallet':
                return `${obj.__typename}:${obj.accountNumber}`;

            case 'Customer':
                return `${obj.__typename}:${obj.userGuid}`;

            case 'UpsLabel':
                return `${obj.__typename}:${obj.trackingNumber}`;

            case 'Rma':
                return `${obj.__typename}:${obj.requestId}`;

            case 'RmaLineItem':
                return `${obj.__typename}:${obj.requestUnitId}`;

            case 'SelectedPayment':
                return 'Customer:selectedPayment';

            case 'CartStatus':
                return 'Customer:cartStatus';

            case 'RecentSearch':
                return `RecentSearch:${obj.normalizedQuery}`;

            case 'SmsProduct':
            case 'RepairCartLineItem':
                return `${obj.__typename}:${obj.serialId}`;

            default:
                return defaultDataIdFromObject( obj );
        }
    }
} );

const defaults = {
    smsCart: [],
    cartStatus: {
        isUpdating: false,
        __typename: 'CartStatus'
    },
    recentSearches: readPersistedRecentSearches(),
    selectedPayment: {
        paymentId: null,
        billingAddressId: null,
        PONumber: '',
        __typename: 'SelectedPayment'
    }
};

const typeDefs = `
    type RepairCartLineItem: {
        serialId: String!
        equipmentIssue: String!
        injury: Boolean!    
    }
    type RepairsCart: [SmsProduct]
    
    type PaymentOption {
        paymentId: String
        billingAddressId: String
        PONumber: String
    }

    type RecentSearch {
        query: String!
        normalizedQuery: String!
        createdAt: String!
    }
    
    type CartStatus {
        isUpdating: Boolean
    }
    
    Mutation: {
        addRepairItem(input: SmsProductInput!): RepairsCart
        removeRepairItem(input: SmsProductInput!): RepairsCart
        updateRepairItems(input: [SmsProductInput]!): RepairsCart
        setSelectedPaymentOption(input: PaymentOptionInput): PaymentOption,
        setCartStatus(input: CartStatusInput!): CartStatus
        saveRecentSearch(query: String!): [RecentSearch]
        removeRecentSearch(query: String!): [RecentSearch]
    }
    Query {
        smsCart: [SmsProduct]
        recentSearches: [RecentSearch]
        selectedPayment: PaymentOption
        cartStatus: CartStatus
    }
`;

const resolvers = {
// note signature of resolver --> fieldName: (obj, variables, context, info) => result;
// resource --> https://www.apollographql.com/docs/react/essentials/local-state.html
    Mutation: {
        // resource -> https://www.apollographql.com/docs/react/essentials/local-state/#managing-the-cache
        removeRepairItem: ( _, {input}, {cache, getCacheKey} ) => {
            const store = cache.readQuery( {query: GetRepairsCart} );
            const smsCart = store.smsCart.filter( ( {serialId} ) => serialId !== input.serialId );
            cache.writeData( {data: {smsCart}} );

            return input;
        },

        addRepairItem: ( _, {input}, {cache, getCacheKey} ) => {
            const store = cache.readQuery( {query: GetRepairsCart} );
            const itemExists = store.smsCart.find( ( {serialId} ) => serialId === input.serialId );

            // note only allow single add
            if ( !itemExists ) {
                cache.writeData( {data: {smsCart: store.smsCart.concat( input )}} );
            }

            return input;
        },

        updateRepairsCart: ( _, {input}, {cache, getCacheKey} ) => {
            const smsCart = input != null ? input : [];
            cache.writeData( {data: {smsCart}} );
            return smsCart;
        },

        setSelectedPaymentOption: ( _, {input}, {cache, getCacheKey} ) => {
            const store = cache.readQuery( {query: GetSelectedPaymentOption} );
            const newSelectedPayment = {
                ...store.selectedPayment,
                ...input
            };
            cache.writeData( {data: {selectedPayment: newSelectedPayment}} );

            return newSelectedPayment;
        },

        setCartStatus: ( _, {input}, {cache, getCacheKey} ) => {
            let newCartStatus;
            try {
                const store = cache.readQuery( {query: GetCartStatus} );
                newCartStatus = {
                    ...store.cartStatus,
                    ...input
                };
            }
            catch ( error ) {
                // Initialize the cartStatus if it doesn't exist in the cache
                newCartStatus = {
                    isUpdating: false,
                    __typename: 'CartStatus',
                    ...input
                };
            }
            cache.writeData( {data: {cartStatus: newCartStatus}} );

            return newCartStatus;
        },

        saveRecentSearch: ( _, {query}, {cache} ) => {
            const trimmedQuery = String( query || '' ).trim();

            if ( !trimmedQuery ) {
                return getRecentSearchesFromStore( cache );
            }

            const normalizedQuery = normalizeRecentSearchQuery( trimmedQuery );
            const recentSearches = getRecentSearchesFromStore( cache )
                .filter( recentSearch => recentSearch.normalizedQuery !== normalizedQuery );
            const nextRecentSearches = [createRecentSearchRecord( trimmedQuery ), ...recentSearches]
                .slice( 0, MAX_RECENT_SEARCHES );

            cache.writeData( {data: {recentSearches: nextRecentSearches}} );
            persistRecentSearches( nextRecentSearches );

            return nextRecentSearches;
        },

        removeRecentSearch: ( _, {query}, {cache} ) => {
            const normalizedQuery = normalizeRecentSearchQuery( query );
            const nextRecentSearches = getRecentSearchesFromStore( cache )
                .filter( recentSearch => recentSearch.normalizedQuery !== normalizedQuery );

            cache.writeData( {data: {recentSearches: nextRecentSearches}} );
            persistRecentSearches( nextRecentSearches );

            return nextRecentSearches;
        }
    }
};

const stateLink = withClientState( {resolvers, cache, defaults, typeDefs} );

export default stateLink;
